import React, {
    useEffect,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { filter } from 'rxjs/operators';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _result from 'lodash/result';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';
import MediaPlaybackWrapper from '@mlb/react-patterns/components/MediaPlayback/mediaPlaybackWrapper.tsx';
import Skeleton from '@mlb/react-patterns/components/Skeleton/skeleton.tsx';

import { useWindowDimensions } from 'bdvp-common/src/hooks';
import { Tracking } from 'bdvp-tracking';

import {
    buildCustomAdRequest,
    getDeviceInfo,
    unsubscribeFromSubscriptions,
    onAdStartedEvent,
    setPlayer,
    setIsPlayerPaused,
    setIsPrerollAdPlaying,
} from './utils';

const PlaybackWrapper = styled(MediaPlaybackWrapper)`
    background-color: ${COLORS.mlbGrey7};

    video,
    .mlb-player-controls-loader--container,
    .mlb-player-controls--loader::after {
        background-color: ${COLORS.mlbGrey7} !important;
    }
`;

const Player = (props) => {

    const {
        club,
        containerId,
        isClubPage,
        isEU,
        isLoading,
        isPlaybackReady,
        lang,
        mbox,
        mediaItem,
        mediaItemList,
        onMetaDataLoaded,
        playerConfig,
        topicId,
        setPlayerWrapper,
    } = props;
    const { width } = useWindowDimensions();
    const isLargeBp = useRef(false);
    const subscriptions = useRef([]);
    const mediaItemRef = useRef(mediaItem);

    useEffect(() => {

        return (() => {

            unsubscribeFromSubscriptions(subscriptions.current);
            setPlayer(null);
            setIsPlayerPaused(null);
            setPlayerWrapper(null);
            setIsPrerollAdPlaying(null);

        });

    }, []);

    useEffect(() => {

        isLargeBp.current = width >= parseInt(BREAKPOINTS.lg);

    }, [width]);

    useEffect(() => {

        mediaItemRef.current = mediaItem;

    }, [mediaItem]);

    if (!isPlaybackReady || isLoading) {

        return (
            <Skeleton
                isLoading
                width={'100%'}
                height={'100%'}
                borderRadius={0}
            />
        );

    }

    const autoPlay = _get(playerConfig, 'autoPlay', true);
    const mediaConfig = _get(playerConfig, 'mediaConfig', {});
    const initConfig = _get(playerConfig, 'initConfig', {});
    const playerOptions = _get(playerConfig, 'playerOptions', {});
    const deviceInfo = getDeviceInfo();
    const mediaConfigClone = { ...mediaConfig };

    _set(mediaConfigClone, 'videocore.advertising.dfp.isEu', isEU);

    return (
        <PlaybackWrapper
            autoPlay={autoPlay}
            handleEmbedWrapper={(embedWrapper) => {

                setPlayerWrapper(embedWrapper);

            }}
            handleMediaPlayerReady={(mediaPlayer) => {

                setPlayer(mediaPlayer);

            }}
            handleObservable={(observationCenter) => {

                subscriptions.current.push(
                    observationCenter.stateSubject.subscribe((data) => {

                        if(data.type && data.type !== 'empty' && data.stateModel) {

                            setIsPlayerPaused(Boolean(data.stateModel.paused));

                        }

                    })
                );

                subscriptions.current.push(
                    observationCenter.embedSubject.pipe(filter((event) => {

                        return event.type === 'metaDataLoaded';

                    })).subscribe((message) => {

                        onMetaDataLoaded(mediaItemRef.current, _get(message, 'data.data'));

                    })
                );
                subscriptions.current.push(
                    observationCenter.embedSubject.pipe(filter((event) => {

                        return event.type === 'showLeavebehind';

                    })).subscribe((message) => {

                        Tracking.trackShowLeaveBehind(_get(message, 'prevClip.data.slug'), _get(message, 'nextClip.data.slug'));

                    })
                );
                subscriptions.current.push(
                    observationCenter.embedSubject.pipe(filter((event) => {

                        return event.type === 'leaveBehindNextClipClick';

                    })).subscribe((message) => {

                        Tracking.trackLeaveBehindNextClipClick(_get(message, 'prevClip.data.slug'), _get(message, 'nextClip.data.slug'));

                    })
                );
                subscriptions.current.push(
                    observationCenter.embedSubject.pipe(filter((event) => {

                        return event.type === 'mediaReplay';

                    })).subscribe((message) => {

                        Tracking.trackLeaveBehindReplay(_get(message, 'prevClip.data.slug'), _get(message, 'nextClip.data.slug'));

                    })
                );
                subscriptions.current.push(
                    observationCenter.embedSubject.pipe(filter((event) => {

                        return event.type === 'leaveBehindCountdownComplete';

                    })).subscribe((message) => {

                        Tracking.trackLeaveBehindCountdownComplete(_get(message, 'prevClip.data.slug'), _get(message, 'nextClip.data.slug'));

                    })
                );
                subscriptions.current.push(
                    observationCenter.pluginSubject.pipe(filter((event) => {

                        return event.type === 'adStartedEvent';

                    })).subscribe((message) => {

                        setIsPrerollAdPlaying(true);
                        onAdStartedEvent(_get(message, 'config.dfp'), _result(message, 'adEvent.getAd'), isLargeBp.current);

                    })
                );
                subscriptions.current.push(
                    observationCenter.pluginSubject.pipe(filter((event) => {

                        return event.type === 'adCompleteEvent';

                    })).subscribe(() => {

                        setIsPrerollAdPlaying(false);

                    })
                );
                subscriptions.current.push(
                    observationCenter.embedSubject.pipe(filter((event) => {

                        return event.type === 'mediaStart';

                    })).subscribe(() => {

                        Tracking.trackVideoStart({
                            title: _get(mediaItemRef, 'current.title', ''),
                            slug: _get(mediaItemRef, 'current.slug', ''),
                            duration: _get(mediaItemRef, 'current.duration', ''),
                        });

                        const translationId = _get(mediaItemRef, 'current.translationId', '');
                        if (translationId) {

                            Tracking.trackTranslationIdForVideoStart(translationId);

                        }

                    })
                );
                subscriptions.current.push(
                    observationCenter.embedSubject.pipe(filter((event) => {

                        return event.type === 'mediaComplete';

                    })).subscribe(() => {

                        const duration = _get(mediaItemRef, 'current.duration', '');
                        const playtimeSeconds = duration && duration.split(':').reduce((acc, time) => isNaN(time) ? 0 : (60 * acc) + +time, 0) || 0;

                        Tracking.trackVideoComplete({
                            title: _get(mediaItemRef, 'current.title', ''),
                            slug: _get(mediaItemRef, 'current.slug', ''),
                            duration,
                            playtimeSeconds,
                        });

                    })
                );

            }}
            initConfig={{
                ...initConfig,
                dynamicAdConfig: {
                    customAdRequestBuilder: (adConfig, clip, cuePointVO) => {

                        return buildCustomAdRequest(adConfig, clip, cuePointVO, isLargeBp.current, isClubPage, topicId, mbox);

                    },
                },
            }}
            mediaConfig={mediaConfigClone}
            playbackContainerId={containerId}
            playerOptions={{
                ...playerOptions,
                metaData: mediaItem,
                list: mediaItemList.filter(item => item.slug !== mediaItem.slug),
                leaveBehindEnabled: true,
                club,
                deviceInfo,
                deviceFormFactor: _get(deviceInfo, 'formFactor'),
                isClubPage,
                lang,
                mbox,
            }}
        />
    );

};

Player.propTypes = {
    club: PropTypes.string.isRequired,
    containerId: PropTypes.string.isRequired,
    isClubPage: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isPlaybackReady: PropTypes.bool.isRequired,
    lang: PropTypes.string.isRequired,
    mediaItem: PropTypes.shape().isRequired,
    mediaItemList: PropTypes.array.isRequired,
    onMetaDataLoaded: PropTypes.func.isRequired,
    playerConfig: PropTypes.shape().isRequired,
    topicId: PropTypes.string.isRequired,
    isEU: PropTypes.bool,
    mbox: PropTypes.shape(),
    setPlayerWrapper: PropTypes.func.isRequired,
};

Player.defaultProps = {
    isEU: false,
    mbox: {},
};

export default Player;
